/**
 * AddDatasetPage provides functionality for displaying, adding, and editing datasets.
 * It serves as the main container for dataset management operations.
 */

import React, { useState, useEffect } from "react";
import Datagrid from "./Datagrid";
import InputForm from "./InputForm";
import EditForm from "./EditForm";
import { useAuth } from "hooks/useAuth";
import { osirisApi } from "services/api/osiris";
import PyramidCard from "components/common/pyramid/PyramidCard";

/**
 * AddDataset component handles dataset management operations including
 * listing, adding, and editing datasets.
 * It orchestrates interactions between the Datagrid, InputForm, and EditForm components.
 */
const AddDataset = () => {
  /**
   * API service for dataset operations
   */
  const osiris = osirisApi();

  /**
   * Authentication context providing user information and access token
   */
  const { accessToken, user, userList, companyList } = useAuth();

  /**
   * State management for the component
   */
  const [datasets, setDatasets] = useState([]); // List of datasets to display
  const [isModalOpen, setIsModalOpen] = useState(false); // Controls visibility of the add dataset modal
  const [editingDataset, setEditingDataset] = useState(null); // Currently selected dataset for editing
  const [isLoading, setIsLoading] = useState(false); // Loading state for external LoadPanel

  /**
   * Fetches datasets from the API
   * Sets loading states and handles errors
   */
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await osiris.getDatasets({
        onlyReady: false,
        includeUserTransitiveGroups: true,
      });
      const datasets = Array.isArray(data) ? data : data.result || [];
      setDatasets(datasets);
    } catch (error) {
      console.error("Error fetching datasets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Effect to fetch datasets when the component mounts
   * or when the access token changes
   */
  useEffect(() => {
    fetchData();
  }, [accessToken]);

  /**
   * Handles clicking on a row in the datagrid
   * Sets the selected dataset for editing
   *
   * @param {Object} rowData - Data of the clicked row
   */
  const handleRowClick = (rowData) => {
    setEditingDataset(rowData);
  };

  /**
   * Handles cancellation of the edit operation
   * Clears the selected dataset
   */
  const handleEditCancel = () => {
    setEditingDataset(null);
  };

  /**
   * Handles confirmation of dataset edits
   * Updates the dataset in the backend and refreshes the data
   *
   * @param {Object} updatedData - The updated dataset information
   */
  const handleEditConfirm = async (updatedData) => {
    try {
      await osiris.updateDataset(updatedData);
      await fetchData();
      setEditingDataset(null);
    } catch (error) {
      console.error("Error updating dataset:", error);
    }
  };

  const handleDownload = (e) => {
    const link = document.createElement("a");
    link.href = "/help/Osiris-Input.pdf";
    link.download = "Osiris-Input.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ position: "relative" }}>
      <Datagrid
        userList={userList}
        companyList={companyList}
        datasets={datasets}
        isLoading={isLoading}
        onAddButtonClick={() => setIsModalOpen(true)}
        onRefresh={fetchData}
        onDownload={handleDownload}
        onRowClick={handleRowClick}
      />

      {/* Input form for adding a new dataset */}
      {isModalOpen && (
        <InputForm
          isOpen={isModalOpen}
          onSuccess={() => {
            setIsModalOpen(false);
            fetchData();
          }}
          accessToken={accessToken}
        />
      )}

      {/* Edit form for modifying an existing dataset */}
      <EditForm
        isVisible={Boolean(editingDataset)}
        onCancel={handleEditCancel}
        onConfirm={handleEditConfirm}
        dataset={editingDataset}
        currentUserId={user?.id}
      />
    </div>
  );
};

/**
 * AddDatasetPage component wraps the AddDataset component in a PyramidCard
 * for consistent styling and presentation within the application.
 */
const AddDatasetPage = () => {
  return (
    <PyramidCard title="Dataset">
      <AddDataset />
    </PyramidCard>
  );
};

export default AddDatasetPage;
